import { useQuery } from '@tanstack/react-query';
import { Preferences } from '@demind-inc/core';

import { accountApi } from '../api';
import { useMemo } from 'react';

interface UsePreferencesParams {
  preferenceId: string;
}

interface UsePreferencesQueryParams {
  queryKey: ['lifestack.preference', UsePreferencesParams];
}

export const usePreferences = ({ preferenceId }: UsePreferencesParams) => {
  const preferencesQuery = useQuery({
    queryKey: ['lifestack.preference', { preferenceId }],
    queryFn: ({ queryKey }: UsePreferencesQueryParams) => {
      const [, { preferenceId }] = queryKey;
      return accountApi.getPreferences(preferenceId).then(({ data }) => data as Preferences);
    },
    refetchOnWindowFocus: false,
    staleTime: 60 * 60 * 1000, // 1 hour
    enabled: !!preferenceId,
  });

  const prefs = useMemo(() => {
    const rawPrefs = preferencesQuery.data as unknown as Record<string, unknown> | undefined;
    return {
      calendarPreferences: preferencesQuery.data?.calendar ?? {},
      energyPreferences: preferencesQuery.data?.energy ?? {},
      generalPreferences: preferencesQuery.data?.general ?? {},
      energyBoostPreferences: preferencesQuery.data?.energyBoost ?? {},
      schedulerPreferences: preferencesQuery.data?.scheduler ?? {},
      mealPreferences: preferencesQuery.data?.meal ?? {},
      workoutPreferences: (rawPrefs?.workout ?? {}) as Record<string, unknown>,
      locationPreferences: (rawPrefs?.location ?? {}) as Record<string, unknown>,
      emailPreferences: preferencesQuery.data?.email ?? {},
      taskPreferences: preferencesQuery.data?.task ?? {},
    };
  }, [preferencesQuery.data]);

  return {
    ...prefs,
    ...preferencesQuery,
  };
};
